import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
  ReactFlow,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  MiniMap,
  MarkerType,
  ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import CustomNode from "./CustomNode";
import SummaryPanel from "./SummaryPanel";
import {
  RF_STYLE,
  DEFAULT_PALETTE,
  MD_STYLE,
  parseMermaid,
  buildFlowForCollapsedView,
} from "./mermaidHelper.js";

export default function ReactFlowFromMermaid({
  mermaidDiagram,
  summaries = [],
  palette = DEFAULT_PALETTE,
  handleSelectMermaid,
  mermaidTypes,
  selectedMeramaidName,
}) {
  const parsed = useMemo(() => parseMermaid(mermaidDiagram), [mermaidDiagram]);
  const groupIds = useMemo(() => Object.keys(parsed.groups), [parsed]);
  const [panelOpen, setPanelOpen] = useState(true);
  // By default, input/output groups are expanded
  const initialExpanded = new Set();
  groupIds.forEach((g) => {
    const label = parsed.groups[g].label || g;
    if (
      label &&
      (label.toLowerCase().includes("input") ||
        label.toLowerCase().includes("output"))
    )
      initialExpanded.add(g);
  });

  const [expandedGroups, setExpandedGroups] = useState(initialExpanded);
  const [selectedNode, setSelectedNode] = useState(null);

  const { flowNodes: initialNodes, flowEdges: initialEdges } = useMemo(
    () => buildFlowForCollapsedView(parsed, summaries, palette, expandedGroups),
    [parsed, summaries, palette, expandedGroups, selectedNode]
  );

  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);

  // sync when expandedGroups changes
  useEffect(() => {
    const { flowNodes, flowEdges } = buildFlowForCollapsedView(
      parsed,
      summaries,
      palette,
      expandedGroups,
      selectedNode
    );
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [parsed, summaries, palette, expandedGroups, selectedNode]);

  const onNodeClick = useCallback(
    (evt, node) => {
      // group toggle handling
      if (node.data && node.data.isGroup) {
        const gid = node.data.groupId;
        const label = parsed.groups?.[gid]?.label || gid;
        if (
          label &&
          (label.toLowerCase().includes("input") ||
            label.toLowerCase().includes("output"))
        ) {
          // IO groups are non-collapsible
          setSelectedNode(null);
          return;
        }
        setExpandedGroups((prev) => {
          const next = new Set(Array.from(prev));
          if (next.has(gid)) next.delete(gid);
          else next.add(gid);
          return next;
        });
        setSelectedNode(null);
        return;
      }

      setSelectedNode(node);
      setPanelOpen(true);
    },
    [parsed]
  );

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );
  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );
  const onConnect = useCallback(
    (connection) => setEdges((eds) => addEdge(connection, eds)),
    []
  );

  const defaultEdgeOptions = {
    markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 10 },
  };
  const nodeTypes = useMemo(() => ({ customNode: CustomNode }), []);
  if (!mermaidDiagram || mermaidDiagram.trim() === "") {
    return (
      <div style={{ width: "100%", height: "100%" }} className="flex items-center justify-center h-64 gap-2 text-gray-400">
        <span className="text-sm">No data available for this asset.</span>
      </div>
    );
  }
  return (
    <ReactFlowProvider>
      <div
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          display: "flex",
          background: palette.background,
        }}
      >
        <div style={{ flex: 1 }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            fitView
            style={RF_STYLE}
            defaultEdgeOptions={defaultEdgeOptions}
            nodeTypes={nodeTypes}
            zoomOnScroll={false}
            zoomActivationKeyCode="Control"
          >
            {mermaidTypes.length > 0 && (
              <div className="mb-3">
                <select
                  name="mermaidName"
                  value={selectedMeramaidName}
                  onChange={(e) => handleSelectMermaid(e.target.value)}
                  className="border rounded p-2"
                  style={MD_STYLE}
                >
                  {mermaidTypes.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <Controls
              position="bottom-right"
              showZoom={true}
              showFitView={true}
              showInteractive={true}
            />
            {/* <MiniMap position="bottom-left" /> */}
            <Background />
          </ReactFlow>
        </div>
        <SummaryPanel
          panelOpen={panelOpen}
          setPanelOpen={setPanelOpen}
          selectedNode={selectedNode}
          setSelectedNode={setSelectedNode}
        />
      </div>
    </ReactFlowProvider>
  );
}
